import React from "react";
import "../../components/styles/CrosstabTable.css";

export default function CrosstabTable({ data }) {
  if (!data || !data.rows || !data.columns || !data.matrix) {
    return <div>No Crosstab Data</div>;
  }

  const { rows, columns, matrix } = data;

  /* ======================================================
     REMOVE __MISSING__
  ====================================================== */

  const validColumns = columns.filter(
    col => col && !col.includes("__MISSING__")
  );

  const validRowIndexes = rows
    .map((row, idx) => {
      if (!row) return null;
      if (row.includes("__MISSING__")) return null;
      return idx;
    })
    .filter(idx => idx !== null);

  /* ======================================================
     ROW HIERARCHY (Female > Delhi NCR)
  ====================================================== */

  const splitRows = validRowIndexes.map(idx => {
    const parts = rows[idx].split(">").map(s => s.trim());
    return {
      parent: parts[0],
      child: parts[1] || "",
      rowIndex: idx
    };
  });

  const groupedRows = splitRows.reduce((acc, r) => {
    if (!acc[r.parent]) acc[r.parent] = [];
    acc[r.parent].push(r);
    return acc;
  }, {});

  /* ======================================================
     COLUMN HIERARCHY (Delhi NCR > METRO x SOUTH)
  ====================================================== */

  const splitColumns = validColumns.map(col => {
    const parts = col.split(">").map(s => s.trim());
    return {
      parent: parts[0],
      child: parts[1] || "",
      key: col
    };
  });

  const groupedColumns = splitColumns.reduce((acc, c) => {
    if (!acc[c.parent]) acc[c.parent] = [];
    acc[c.parent].push(c);
    return acc;
  }, {});

  const orderedColumns = [];
  Object.values(groupedColumns).forEach(items =>
    items.forEach(c => orderedColumns.push(c))
  );

  /* ======================================================
     BASE CALCULATION
  ====================================================== */

  const columnBases = {};
  let totalBase = 0;

  orderedColumns.forEach(({ key }) => {
    let sum = 0;
    validRowIndexes.forEach(rIndex => {
      const cell = matrix[rIndex]?.[key];
      if (cell?.value != null) sum += cell.value;
    });
    columnBases[key] = sum;
    totalBase += sum;
  });

  /* ======================================================
     RENDER
  ====================================================== */

  return (
    <div className="crosstab-wrapper">
      <table className="crosstab-table">

        {/* ================= HEADER ================= */}
        <thead>
          {/* Parent column header */}
          <tr>
            <th rowSpan={2} id="blank"></th>
            <th rowSpan={2}>Total</th>

            {Object.entries(groupedColumns).map(([parent, items]) =>
              items.length > 1 ? ( // Only show parent if more than 1 child
                <th key={parent} colSpan={items.length} className="group-col">
                  {parent}
                </th>
              ) : (
                <th key={parent} rowSpan={2} className="group-col">
                  {parent} {/* No children, span both rows */}
                </th>
              )
            )}
          </tr>

          {/* Child column header */}
          <tr>
            {orderedColumns.map(col =>
              col.child ? (
                <th key={col.key} className="child-col">
                  {col.child}
                </th>
              ) : null // Skip child header if no child
            )}
          </tr>
        </thead>

        {/* ================= BODY ================= */}
        <tbody>

          {/* Base row */}
          <tr className="base-row">
            <td className="row-header">
              <i>Base (Un-weighted)</i>
            </td>
            <td><b>{totalBase.toFixed(0)}</b></td>

            {orderedColumns.map(col => (
              <td key={col.key}>
                <b>{columnBases[col.key].toFixed(0)}</b>
              </td>
            ))}
          </tr>

          {/* Data rows */}
          {Object.entries(groupedRows).map(([parent, items]) => (
            <React.Fragment key={parent}>

              {/* Parent row */}
              <tr className="group-row">
                <td className="row-header"><b>{parent}</b></td>
                <td></td>
                {orderedColumns.map(col => (
                  <td key={col.key}></td>
                ))}
              </tr>

              {/* Child rows */}
              {items.map(({ child, rowIndex }) => {
                const rowData = matrix[rowIndex];

                const totalPercent = orderedColumns.reduce(
                  (sum, c) => sum + (rowData[c.key]?.percent ?? 0),
                  0
                );

                const totalValue = orderedColumns.reduce(
                  (sum, c) => sum + (rowData[c.key]?.value ?? 0),
                  0
                );

                return (
                  <React.Fragment key={`${parent}-${child}`}>

                    {/* Percent row */}
                    <tr>
                      <td className="row-header child-row">{child}</td>
                      <td>{totalPercent.toFixed(0)}%</td>

                      {orderedColumns.map(col => (
                        <td key={col.key}>
                          {(rowData[col.key]?.percent ?? 0).toFixed(0)}%
                        </td>
                      ))}
                    </tr>

                    {/* Value row */}
                    <tr>
                      <td></td>
                      <td>{totalValue.toFixed(0)}</td>

                      {orderedColumns.map(col => (
                        <td key={col.key}>
                          {(rowData[col.key]?.value ?? 0).toFixed(0)}
                        </td>
                      ))}
                    </tr>

                  </React.Fragment>
                );
              })}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}
